import useConfirmationAlertDialog from '@act/react-components/dialog-forms/hooks/useConfirmationAlertDialog.hook';
import { CallDto } from '@act/shared/data-transfer-objects';
import { formatPhoneNumber } from '@act/shared/utilities';
import {
  Button,
  FlexContainer,
  IconButton,
  IconLibrary,
  ReemaThemeV2,
} from '@act/storybook';
import { Typography } from '@mui/material';
import { TrackingProvider } from 'react-components/tracking';
import { callsRequests } from 'redux/requests/calls.request';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { utilityAppSelectors } from 'redux/utils/selectors/utilitySelectors';
import { fullName } from 'utilities/formatters/DisplayName';
import { getStatusColor } from './activeCallConnection.util';
import { CALL_CONNECTION_STATUSES_TO_IGNORE } from './activeCallMenu.constants';

export interface ConnectionCardProps {
  connection: CallDto['connections'][0];
  activeConnectionsCount: number;
  showBorder?: boolean;
  verticalCards?: boolean;
}

/**
 * Single participant row in the active call menu.
 *
 * @param props Connection and layout flags.
 * @returns Connection card UI.
 */
export const ConnectionCard = ({
  connection,
  activeConnectionsCount,
  showBorder = false,
  verticalCards = false,
}: ConnectionCardProps) => {
  const openConfirmationDialog = useConfirmationAlertDialog();
  const user = utilityAppSelectors.selectUserOrMemberById(connection.userId);
  const resourceTitle =
    typeof connection.config?.title === 'string'
      ? connection.config.title
      : undefined;
  const connectionDisplayName = user
    ? fullName(user)
    : resourceTitle || 'Unknown';

  const isOnHold = connection.onHold;
  const isConnected = !!connection.connectedAt && !connection.completedAt;
  const isRinging =
    !connection.connectedAt &&
    !connection.completedAt &&
    !(CALL_CONNECTION_STATUSES_TO_IGNORE as readonly string[]).includes(
      connection.status
    );

  const trackingContextName = user
    ? 'connection-card'
    : 'unknown-connection-card';

  const status = connection.status?.toLowerCase();
  const isFailed =
    status === 'failed' ||
    status === 'busy' ||
    status === 'no-answer' ||
    status === 'canceled' ||
    (!!connection.completedAt && !connection.connectedAt);

  const isCanceled = CALL_CONNECTION_STATUSES_TO_IGNORE.includes(
    connection.status as (typeof CALL_CONNECTION_STATUSES_TO_IGNORE)[number]
  );
  const isCompleted = !!connection.completedAt;

  const [_toggledHold, isTogglingHold, toggleConnectionHold] = useApiCall(
    callsRequests.toggleConnectionHold
  );
  const [_hangupConnection, isHangingUp, hangupConnection] = useApiCall(
    callsRequests.hangupConnection
  );

  const handleHold = () => {
    toggleConnectionHold({
      body: {
        connectionId: connection.id,
        onHold: !isOnHold,
      },
    });
  };

  const handleHangup = async () => {
    let message: string;
    let title: string;
    let confirmationText: string;

    if (isRinging) {
      title = 'Cancel call confirmation';
      message = `Are you sure you want to cancel the call to ${connectionDisplayName}?`;
      confirmationText = 'Cancel call';
    } else {
      title = 'Hang up call confirmation';
      const willCallContinue = activeConnectionsCount >= 3;
      message = willCallContinue
        ? `${connectionDisplayName} will leave the call and the existing participants will continue their call.`
        : `Are you sure you want to remove ${connectionDisplayName}?`;
      confirmationText = 'Hang up';
    }

    const shouldHangup = await openConfirmationDialog({
      title,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      icon: <IconLibrary iconName="Warning" size={42} color="white" />,
      message,
      confirmationText,
      confirmButtonColor: 'error',
    });
    if (shouldHangup) {
      hangupConnection({
        body: {
          connectionId: connection.id,
        },
      });
    }
  };

  let iconName = 'PhoneCall';
  if (isFailed) {
    iconName = 'PhoneX';
  } else if (isCanceled || isCompleted) {
    iconName = 'PhoneEnded';
  }

  const cardFlexBasis = showBorder
    ? verticalCards
      ? 'calc(100% - 42px)'
      : 'calc(50% - 42px)'
    : '300px';

  const strokeColor = ReemaThemeV2.LIGHT_MODE.palette.greyShades[250];

  return (
    <TrackingProvider contextName={trackingContextName}>
      <FlexContainer
        direction="row"
        alignItems="center"
        gap={1.5}
        padding={showBorder ? 2 : 1}
        backgroundColor="white"
        minWidth="280px"
        flexGrow={showBorder ? 0 : 1}
        flexShrink={showBorder ? 0 : 1}
        flexBasis={cardFlexBasis}
        border={showBorder ? `1px solid ${strokeColor}` : undefined}
        borderRadius={showBorder ? 'slightlyRounded' : undefined}
        position="relative"
        opacity={isCompleted ? 0.5 : undefined}
      >
        {isCompleted && (
          <FlexContainer
            direction="row"
            height="auto"
            insetFill
            backgroundColor="rgba(128, 128, 128, 0.3)"
            borderRadius={showBorder ? 'slightlyRounded' : undefined}
            pointerEvents="none"
            zIndex={1}
          />
        )}
        <FlexContainer
            direction="row"
            alignItems="center"
            justifyContent="center"
            width="40px"
            height="40px"
            borderRadius="rounded"
            backgroundColor="rgba(0, 0, 0, 0.05)"
            overflow="visible"
            overflowAllVisible
          >
            <IconLibrary
              iconName={isOnHold ? 'PhonePause' : iconName}
              size={20}
              weight="regular"
              color={isOnHold ? 'warning' : getStatusColor(connection?.status)}
            />
          </FlexContainer>

          <FlexContainer
            direction="column"
            flexGrow={1}
            flexBasis="0"
            minWidth="0"
            overflow="visible"
            overflowAllVisible
          >
            <Typography
              variant="bodyMedium"
              fontWeight={600}
              color={isFailed ? 'error.main' : 'text.primary'}
              noWrap
            >
              {connectionDisplayName}
            </Typography>
            <Typography
              variant="bodySmall"
              color={isFailed ? 'error.main' : 'text.secondary'}
              noWrap
            >
              {formatPhoneNumber(connection.phoneNumber)}
            </Typography>
          </FlexContainer>

          {isFailed && (
            <Typography
              variant="bodySmall"
              color="error.main"
              fontWeight={500}
              noWrap
            >
              Unable to connect
            </Typography>
          )}

          {(isConnected || isRinging) && (
            <>
              <FlexContainer
                direction="row"
                gap={1}
                alignItems="center"
                overflow="visible"
                overflowAllVisible
              >
                <IconButton
                  onClick={handleHangup}
                  color="error"
                  active
                  size="large"
                  disabled={isHangingUp}
                  aria-label={isRinging ? 'Cancel call' : 'Hang up'}
                  trackingName={
                    isRinging
                      ? 'cancel-ringing-connection-button'
                      : 'hangup-connection-button'
                  }
                >
                  <IconLibrary
                    iconName="PhoneEnded"
                    size={18}
                    weight="regular"
                    color="white"
                  />
                </IconButton>
              </FlexContainer>
              {isConnected && !isOnHold ? (
                <Button
                  onClick={handleHold}
                  variant="outlined"
                  disabled={isTogglingHold}
                  startIcon={
                    <IconLibrary
                      iconName="PhonePause"
                      size={18}
                      weight="regular"
                      color="white"
                    />
                  }
                  aria-label="Hold"
                  trackingName="hold-connection-button"
                >
                  Hold
                </Button>
              ) : isConnected && isOnHold ? (
                <Button
                  onClick={handleHold}
                  variant="contained"
                  disabled={isTogglingHold}
                  startIcon={
                    <IconLibrary iconName="Pause" size={18} color="white" />
                  }
                  aria-label="Resume"
                  trackingName="resume-connection-button"
                >
                  Resume
                </Button>
              ) : null}
            </>
          )}
      </FlexContainer>
    </TrackingProvider>
  );
};
